import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { LEVELS, type SessionSummary } from "@backstory/protocol";
import { api, queryKeys } from "../api";
import { rememberDeletion } from "../features/org/deletions";
import { useCan } from "../auth/roles";
import { authStore, useAuth } from "../auth/store";
import { SessionsTable } from "../components/SessionsTable";
import { EmptySearchMark, EmptySessions, ErrorState } from "../components/States";
import { AutoLoadMore, BusyButton, ConfirmDialog, Dialog, Field, PageHead, fmtNum, useToast } from "../components/ui";
import { activeFilterCount, createRemoteSegment, deleteRemoteSegment, deleteSegment, filtersFromSearch, filtersToSearch, listRemoteSegments, loadSegments, serverParams, type SavedSegment, type SessionFilters } from "../lib/filters";
import { AiBlockedTooltip, NlSearch, useAiCapability } from "../features/ai";
import { insightsApi, insightsKeys } from "../features/insights/api";
import { DateField } from "../features/insights/components";
import { customDateBounds } from "../features/insights/range";
import { DeleteWhenFields, deleteWhenBounds, deleteWhenPhrase, type DeleteWhen } from "../components/DeleteWindow";
import { countryName } from "../lib/country";

function listed(values: Array<string | undefined>, current?: string): string[] {
  return Array.from(new Set([...values.filter((v): v is string => !!v), ...(current ? [current] : [])])).sort();
}

/** The sentence the clear confirm shows. Exported for the copy test. An empty window means the dates are not chosen yet. */
export function sessionClearCopy(projectName: string, count: number | undefined, pending: boolean, failed: boolean, when = "from the beginning"): string {
  const name = projectName.trim() || "This project";
  if (!when) return "Choose the first and last day.";
  if (pending) return name === "This project" ? "Checking how many sessions this project has." : `Checking how many sessions ${name} has.`;
  if (failed) return "The number of sessions could not be loaded. Try again.";
  if (!count) return `${name} has no sessions ${when}.`;
  const sessions = count === 1 ? "1 session" : `${fmtNum(count)} sessions`;
  return `${name} has ${sessions}. Every session ${when}, and everything recorded with it, is removed. This includes the replay, events, and files.`;
}

export function SessionsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const can = useCan();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const filters = useMemo(() => filtersFromSearch(search), [search]);
  const [qInput, setQInput] = useState(filters.q ?? "");
  const [showFacets, setShowFacets] = useState(!!filters.country);
  const facetScroll = useRef<HTMLElement | null>(null);
  const facetScrollTop = useRef(0);
  const qc = useQueryClient();
  const toast = useToast();
  const [localSegments, setLocalSegments] = useState<SavedSegment[]>(() => loadSegments(auth.currentProjectId));
  const remoteSegments = useQuery({
    queryKey: ["segments", auth.currentProjectId],
    queryFn: () => listRemoteSegments(auth.currentProjectId!),
    enabled: !!auth.currentProjectId,
    retry: 0,
  });
  const segments = remoteSegments.isSuccess
    ? [...remoteSegments.data, ...localSegments.filter((s) => !remoteSegments.data.some((r) => r.name === s.name))]
    : localSegments;
  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [segName, setSegName] = useState("");
  const [pendingDelete, setPendingDelete] = useState<SavedSegment | null>(null);
  const [pendingSession, setPendingSession] = useState<SessionSummary | null>(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearMode, setClearMode] = useState<DeleteWhen>("all");
  const [clearFrom, setClearFrom] = useState("");
  const [clearTo, setClearTo] = useState("");
  const projectName = auth.projects.find((p) => p.id === auth.currentProjectId)?.name ?? "";
  const clearPhrase = deleteWhenPhrase(clearMode, clearFrom, clearTo);
  const clearBounds = deleteWhenBounds(clearMode, clearFrom, clearTo);
  const clearCount = useQuery({
    queryKey: ["sessions", "count", auth.currentProjectId, clearBounds?.from ?? "", clearBounds?.to ?? ""],
    queryFn: () => api.projectSessionCount(auth.currentProjectId!, clearBounds ?? undefined),
    enabled: clearOpen && !!auth.currentProjectId && !!clearBounds,
  });
  const deletions = useQuery({
    queryKey: queryKeys.deletions(auth.currentOrgId ?? ""),
    queryFn: () => api.orgs.deletions(auth.currentOrgId!),
    enabled: can.admin && !!auth.currentOrgId,
    refetchInterval: (query) => (query.state.data?.some((d) => d.status === "running") ? 1000 : false),
  });
  const running = (deletions.data ?? []).filter((d) => d.status === "running");
  const deletingIds = new Set(running.filter((d) => d.kind === "session").map((d) => d.targetId));
  const sessionWipe = running.find((d) => d.kind === "sessions" && d.targetId === auth.currentProjectId);
  const clearing = !!sessionWipe;
  const clearSessions = useMutation({
    mutationFn: () => api.deleteProjectSessions(auth.currentProjectId!, clearBounds ?? undefined),
    onSuccess: (res) => {
      setClearOpen(false);
      if (auth.currentOrgId) rememberDeletion(qc, auth.currentOrgId, res);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const removeSession = useMutation({
    mutationFn: (id: string) => api.deleteSession(id),
    onSuccess: (res) => {
      setPendingSession(null);
      if (auth.currentOrgId) rememberDeletion(qc, auth.currentOrgId, res);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const [deleting, setDeleting] = useState(false);
  const askMode = search.ask === "1" || search.ask === 1 || search.ask === true;
  const askQuery = typeof search.aq === "string" ? search.aq : "";
  const ai = useAiCapability();
  const migratedEnvironment = useRef(false);

  useLayoutEffect(() => {
    const scroller = facetScroll.current;
    if (!scroller) return;
    const y = facetScrollTop.current;
    scroller.scrollTop = y;
    requestAnimationFrame(() => { scroller.scrollTop = y; });
  }, [showFacets]);
  useEffect(() => setLocalSegments(loadSegments(auth.currentProjectId)), [auth.currentProjectId]);
  const migrated = useRef<string | null>(null);
  useEffect(() => {
    const projectId = auth.currentProjectId;
    if (!projectId || !remoteSegments.isSuccess || migrated.current === projectId) return;
    const local = loadSegments(projectId);
    if (!local.length) {
      migrated.current = projectId;
      return;
    }
    migrated.current = projectId;
    void (async () => {
      const names = new Set((remoteSegments.data ?? []).map((s) => s.name));
      let moved = 0;
      let failed = false;
      for (const seg of local) {
        if (names.has(seg.name)) {
          deleteSegment(projectId, seg.id);
          moved++;
          continue;
        }
        try {
          await createRemoteSegment(projectId, seg);
          deleteSegment(projectId, seg.id);
          moved++;
        } catch {
          failed = true;
        }
      }
      setLocalSegments(loadSegments(projectId));
      if (moved) void qc.invalidateQueries({ queryKey: ["segments", projectId] });
      if (failed) toast.push("error", "Couldn't save filters to the project.");
    })();
  }, [auth.currentProjectId, qc, remoteSegments.data, remoteSegments.isSuccess, toast]);
  useEffect(() => {
    if (migratedEnvironment.current || !auth.currentProjectId || typeof search.environment !== "string" || !search.environment) return;
    migratedEnvironment.current = true;
    authStore.setEnvironment(search.environment);
    const { environment: _environment, ...nextSearch } = search;
    void navigate({ to: "/", search: nextSearch as never, replace: true });
  }, [auth.currentProjectId, navigate, search]);

  const writeSearch = (nextFilters: SessionFilters, ask: { on?: boolean; q?: string } = {}) => {
    const next = filtersToSearch(nextFilters) as Record<string, string | boolean | number>;
    const on = ask.on ?? askMode;
    const q = ask.q ?? askQuery;
    if (on) next.ask = true;
    if (q) next.aq = q;
    void navigate({ to: "/", search: next as never, replace: true });
  };
  const setFilters = (patch: Partial<SessionFilters>) => {
    const next = { ...filters, ...patch };
    for (const k of Object.keys(next) as Array<keyof SessionFilters>) if (next[k] === undefined || next[k] === "" || next[k] === false) delete next[k];
    writeSearch(next);
  };
  const saveFilters = async () => {
    const name = segName.trim();
    if (!name || saving) return;
    const projectId = auth.currentProjectId;
    if (!projectId) {
      toast.push("error", "Choose a project before saving filters.");
      return;
    }
    setSaving(true);
    try {
      await createRemoteSegment(projectId, { name, filters });
      await qc.invalidateQueries({ queryKey: ["segments", projectId] });
      setSegName("");
      setSaveOpen(false);
    } catch (e) {
      toast.push("error", (e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const removeFilters = async (seg: SavedSegment) => {
    const projectId = auth.currentProjectId;
    if (projectId && remoteSegments.isSuccess && remoteSegments.data.some((s) => s.id === seg.id)) {
      try {
        await deleteRemoteSegment(projectId, seg.id);
        await qc.invalidateQueries({ queryKey: ["segments", projectId] });
      } catch (e) {
        toast.push("error", (e as Error).message);
        throw e;
      }
      return;
    }
    setLocalSegments(deleteSegment(projectId, seg.id));
  };
  useEffect(() => {
    if (askMode) return;
    const h = setTimeout(() => {
      if ((filters.q ?? "") !== qInput.trim()) setFilters({ q: qInput.trim() || undefined });
    }, 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput, askMode]);

  const sp = serverParams(filters);
  const query = useInfiniteQuery({
    queryKey: [...queryKeys.sessions(sp), auth.currentProjectId, auth.currentEnvironment],
    queryFn: ({ pageParam }) => api.sessions({ ...sp, cursor: pageParam as string | undefined, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 15_000,
    enabled: typeof search.environment !== "string",
  });
  const features = useQuery({
    queryKey: insightsKeys.flags({ from: filters.from, to: filters.to }, auth.currentProjectId),
    queryFn: () => insightsApi.flags({ from: filters.from, to: filters.to }),
    enabled: showFacets && !!auth.currentProjectId,
    retry: 0,
  });
  const featureNames = features.data?.map((f) => f.flag) ?? [];
  const variantNames = features.data?.find((f) => f.flag === filters.flag)?.values.map((v) => v.value) ?? [];
  const releaseQuery = useQuery({
    queryKey: [...insightsKeys.releases({}, auth.currentProjectId), "sessions-filter"],
    queryFn: () => insightsApi.releases(),
    enabled: !!auth.currentProjectId,
    retry: 0,
  });

  const all = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const sessions = all;
  const deletingShown = useMemo(() => {
    if (!sessionWipe?.rangeFrom && !sessionWipe?.rangeTo) return deletingIds;
    const from = sessionWipe.rangeFrom ? new Date(sessionWipe.rangeFrom).getTime() : Number.NEGATIVE_INFINITY;
    const to = sessionWipe.rangeTo ? new Date(sessionWipe.rangeTo).getTime() : Number.POSITIVE_INFINITY;
    const next = new Set(deletingIds);
    for (const s of sessions) {
      const t = new Date(s.startedAt).getTime();
      if (t >= from && t < to) next.add(s.id);
    }
    return next;
  }, [deletingIds, sessionWipe, sessions]);
  const releases = useMemo(() => {
    const complete = releaseQuery.data?.map((r) => r.release) ?? [];
    return Array.from(new Set(complete.length ? complete : all.map((s) => s.release).filter(Boolean))) as string[];
  }, [all, releaseQuery.data]);
  const browsers = useMemo(() => listed(all.map((s) => s.browser), filters.browser), [all, filters.browser]);
  const countries = useMemo(() => listed(all.map((s) => s.countryCode), filters.country), [all, filters.country]);
  const systems = useMemo(() => listed(all.map((s) => s.os), filters.os), [all, filters.os]);
  const dateBounds = customDateBounds();

  const active = activeFilterCount(filters);

  return (
    <div className="sessions-page">
      <PageHead>
        <div className="page-title">
          <h1>Sessions</h1>
          <span className="spacer" />
          <div className="page-title-actions">
            {can.admin && (
              <button type="button" className="btn danger" onClick={() => { setClearMode("all"); setClearFrom(""); setClearTo(""); setClearOpen(true); }} disabled={!auth.currentProjectId || clearing}>
                {clearing ? "Deleting…" : "Delete sessions"}
              </button>
            )}
            <button className="btn ghost sessions-refresh" onClick={() => void query.refetch()} disabled={query.isFetching} aria-label="Refresh sessions" title="Refresh">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            </button>
          </div>
        </div>
        <div className="toolbar sessions-toolbar">
          {askMode ? (
            <NlSearch query={askQuery} listing={query.isFetching && !query.isFetchingNextPage} onApply={(next, q) => writeSearch(next, { on: true, q })} onClear={() => writeSearch({}, { on: true, q: "" })} />
          ) : (
            <input className="input sessions-search" placeholder="Search URL, title, user…" value={qInput} onChange={(e) => setQInput(e.target.value)} aria-label="Search sessions" autoComplete="off" />
          )}
          <AiBlockedTooltip ai={ai}>
            <button className="btn" aria-pressed={askMode} onClick={() => writeSearch(filters, { on: !askMode })} disabled={ai.blocked} title={ai.titleFor("Search in your own words")} data-testid="ask-toggle">
              <span className="badge ai" style={{ fontSize: 10, padding: "1px 6px", marginRight: 4 }}>AI</span>Ask
            </button>
          </AiBlockedTooltip>
          <button className="btn" aria-pressed={!!filters.hasErrors} onClick={() => setFilters({ hasErrors: !filters.hasErrors })}>
            ⚠ With errors
          </button>
          <button className="btn" aria-pressed={showFacets} onClick={(e) => { const scroller = e.currentTarget.closest(".main") as HTMLElement | null; facetScroll.current = scroller; facetScrollTop.current = scroller?.scrollTop ?? 0; setShowFacets((open) => !open); }} aria-expanded={showFacets} aria-controls="facets">
            Filters {active > 0 && <span className="badge ai">{active}</span>}
          </button>
          {segments.length > 0 && (
            <select className="input sessions-segments" aria-label="Saved filters" value={segments.find((s) => JSON.stringify(filtersToSearch(s.filters)) === JSON.stringify(filtersToSearch(filters)))?.id ?? ""} onChange={(e) => { const s = segments.find((x) => x.id === e.target.value); if (s) { setQInput(s.filters.q ?? ""); void navigate({ to: "/", search: { ...filtersToSearch(s.filters), ...(askMode ? { ask: true, ...(askQuery ? { aq: askQuery } : {}) } : {}) } as never }); } }} autoComplete="off">
              <option value="">Saved filters…</option>
              {segments.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          <span className="spacer" />
          {(active > 0 || filters.q) && (
            <>
              <button className="btn ghost" onClick={() => setSaveOpen(true)}>Save filters</button>
              {!askMode && <button className="btn ghost" onClick={() => { setQInput(""); writeSearch({}); }}>Clear</button>}
            </>
          )}
        </div>
        {showFacets && (
        <div className="facets" id="facets" aria-label="Filters">
          <label>Country<select value={filters.country ?? ""} onChange={(e) => setFilters({ country: e.target.value || undefined })} autoComplete="off"><option value="">Any</option>{countries.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}</select></label>
          <label>Browser<select value={filters.browser ?? ""} onChange={(e) => setFilters({ browser: e.target.value || undefined })} autoComplete="off"><option value="">Any</option>{browsers.map((b) => <option key={b}>{b}</option>)}</select></label>
          <label>OS<select value={filters.os ?? ""} onChange={(e) => setFilters({ os: e.target.value || undefined })} autoComplete="off"><option value="">Any</option>{systems.map((o) => <option key={o}>{o}</option>)}</select></label>
          <label>Device<select value={filters.device ?? ""} onChange={(e) => setFilters({ device: (e.target.value || undefined) as SessionFilters["device"] })} autoComplete="off"><option value="">Any</option><option value="desktop">Desktop</option><option value="mobile">Mobile</option><option value="tablet">Tablet</option>{filters.device && !["desktop", "mobile", "tablet"].includes(filters.device) && <option value={filters.device}>{filters.device}</option>}</select></label>
          <label>Page<input value={filters.urlContains ?? ""} onChange={(e) => setFilters({ urlContains: e.target.value || undefined })} placeholder="contains…" autoComplete="off" /></label>
          <label>Release<select value={filters.release ?? ""} onChange={(e) => setFilters({ release: e.target.value || undefined })} autoComplete="off"><option value="">All releases</option>{releases.map((r) => <option key={r}>{r}</option>)}</select></label>
          <label>Review status<select value={filters.reviewed ?? ""} onChange={(e) => setFilters({ reviewed: (e.target.value || undefined) as SessionFilters["reviewed"] })} autoComplete="off"><option value="">All sessions</option><option value="unreviewed">Not reviewed</option><option value="reviewed">Reviewed</option></select></label>
          <label>Viewed<select value={filters.viewed ?? ""} onChange={(e) => setFilters({ viewed: (e.target.value || undefined) as SessionFilters["viewed"] })} autoComplete="off"><option value="">All sessions</option><option value="unviewed">Not viewed</option><option value="viewed">Viewed</option></select></label>
          <label>Recorder level<select value={filters.level ?? ""} onChange={(e) => setFilters({ level: (e.target.value || undefined) as SessionFilters["level"] })} autoComplete="off"><option value="">Any</option>{LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}</select></label>
          <label>Min duration (s)<input type="number" min={0} value={filters.minDurationSec ?? ""} onChange={(e) => setFilters({ minDurationSec: e.target.value ? Number(e.target.value) : undefined })} autoComplete="off" /></label>
          <label>Max duration (s)<input type="number" min={0} value={filters.maxDurationSec ?? ""} onChange={(e) => setFilters({ maxDurationSec: e.target.value ? Number(e.target.value) : undefined })} autoComplete="off" /></label>
          <label>From<DateField label="From" committed={filters.from ?? ""} min={dateBounds.min} max={filters.to && filters.to < dateBounds.max ? filters.to : dateBounds.max} onCommit={(from) => setFilters({ from, to: from && filters.to && from > filters.to ? from : filters.to })} /></label>
          <label>To<DateField label="To" committed={filters.to ?? ""} min={filters.from && filters.from > dateBounds.min ? filters.from : dateBounds.min} max={dateBounds.max} onCommit={(to) => setFilters({ from: to && filters.from && to < filters.from ? to : filters.from, to })} /></label>
          <label>Feature<input list="session-features" value={filters.flag ?? ""} onChange={(e) => setFilters({ flag: e.target.value.trim() || undefined, flagValue: undefined })} placeholder="Any" autoComplete="off" /><datalist id="session-features">{featureNames.map((n) => <option key={n} value={n} />)}</datalist></label>
          <label>Variant<input list="session-variants" value={filters.flagValue ?? ""} onChange={(e) => setFilters({ flagValue: e.target.value.trim() || undefined })} placeholder={filters.flag ? "Any variant" : "Choose a feature"} disabled={!filters.flag} autoComplete="off" /><datalist id="session-variants">{variantNames.map((n) => <option key={n} value={n} />)}</datalist></label>
          <label>End user<input value={filters.user ?? ""} onChange={(e) => setFilters({ user: e.target.value || undefined })} placeholder="contains…" autoComplete="off" /></label>
          <label style={{ alignSelf: "end" }}><span /><button className="btn" aria-pressed={!!filters.hasGaps} onClick={() => setFilters({ hasGaps: !filters.hasGaps })}>Has gaps</button></label>
        </div>
        )}
      </PageHead>

      <div className="card sessions-results-card">
        {query.isError ? (
          <ErrorState error={query.error} retry={() => void query.refetch()} />
        ) : query.isSuccess && sessions.length === 0 ? (
          active > 0 || filters.q ? <div className="empty"><EmptySearchMark /><h3>No sessions match these filters</h3></div> : <EmptySessions />
        ) : (
          <SessionsTable
            sessions={sessions}
            loading={query.isPending || query.isFetchingNextPage}
            onOpen={(id) => void navigate({ to: "/sessions/$id", params: { id }, state: { sessionsSearch: search } as never })}
            onDelete={can.admin ? setPendingSession : undefined}
            deletingIds={deletingShown}
            deletingAll={!!sessionWipe && !sessionWipe.rangeFrom && !sessionWipe.rangeTo}
          />
        )}
        <AutoLoadMore hasMore={!!query.hasNextPage} loading={query.isFetchingNextPage} loadMore={() => void query.fetchNextPage()} label="Loading more sessions" />
      </div>

      <Dialog open={saveOpen} onClose={() => { if (!saving) setSaveOpen(false); }} title="Save filters" footer={<><button className="btn" onClick={() => setSaveOpen(false)} disabled={saving}>Cancel</button><BusyButton className="btn primary" busy={saving} busyLabel="Saving…" disabled={!segName.trim()} onClick={() => void saveFilters()}>Save</BusyButton></>}>
        <Field label="Name" hint="Saved for everyone on this project.">{(p) => <input {...p} className="input" value={segName} onChange={(e) => setSegName(e.target.value)} autoFocus />}</Field>
        {segments.length > 0 && (
          <div className="pill-row">
            {segments.map((s) => (
              <span key={s.id} className="chip">
                {s.name}
                <button aria-label={`Delete saved filters ${s.name}`} onClick={() => setPendingDelete(s)}>✕</button>
              </span>
            ))}
          </div>
        )}
      </Dialog>
      <ConfirmDialog
        open={!!pendingDelete}
        danger
        title="Delete saved filter"
        confirmLabel="Delete saved filter"
        busy={deleting}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
        onConfirm={() => {
          const seg = pendingDelete;
          if (!seg || deleting) return;
          setDeleting(true);
          void removeFilters(seg).then(() => setPendingDelete(null)).finally(() => setDeleting(false));
        }}
      >
        <p style={{ margin: 0 }}>{pendingDelete ? `${pendingDelete.name} is removed for everyone on this project.` : ""}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={!!pendingSession}
        danger
        title="Delete session"
        confirmLabel="Delete session"
        busy={removeSession.isPending}
        onClose={() => { if (!removeSession.isPending) setPendingSession(null); }}
        onConfirm={() => {
          if (!pendingSession || removeSession.isPending) return;
          removeSession.mutate(pendingSession.id);
        }}
      >
        <p style={{ margin: 0 }}>
          {pendingSession ? `Session ${pendingSession.id} and everything recorded with it is removed. This includes the replay, events, and files.` : ""}
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        open={clearOpen}
        danger
        title="Delete sessions"
        confirmLabel="Delete sessions"
        busy={clearSessions.isPending}
        disabled={!clearBounds || clearCount.isPending || clearCount.isError || (clearCount.data?.count ?? 0) === 0}
        onClose={() => { if (!clearSessions.isPending) setClearOpen(false); }}
        onConfirm={() => {
          if (!auth.currentProjectId || !clearBounds || clearSessions.isPending || (clearCount.data?.count ?? 0) === 0) return;
          clearSessions.mutate();
        }}
      >
        <DeleteWhenFields mode={clearMode} from={clearFrom} to={clearTo} onMode={setClearMode} onFrom={setClearFrom} onTo={setClearTo} />
        <p className="delete-when-note">{sessionClearCopy(projectName, clearBounds ? clearCount.data?.count : undefined, !!clearBounds && clearCount.isPending, !!clearBounds && clearCount.isError, clearPhrase)}</p>
      </ConfirmDialog>
    </div>
  );
}
